'use client'

/**
 * Application footer — fine-tech instrument bookend (ui-ux-pro-max density 8).
 *
 * Zones: copyright · legal/help links · US equity + systems status · top.
 * Materials match navbar/subnav: flat ivory, hairline, mono instrument line.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import Link from 'next/link'
import { useTheme } from '@/shared/theme'
import { getUsEquitySessionState } from '@/shared/utils/market/usEquitySession'
import chrome from './AppChrome.module.css'

interface FooterProps {
  className?: string
  style?: React.CSSProperties
}

type SystemStatus = 'ok' | 'warn' | 'down' | 'maintenance'

function resolveStatus(raw: string): SystemStatus {
  const v = raw.toLowerCase()
  if (v === 'down' || v === 'outage' || v === 'error') return 'down'
  if (v === 'warn' || v === 'degraded' || v === 'warning') return 'warn'
  if (v === 'maintenance') return 'maintenance'
  return 'ok'
}

const STATUS_LABEL: Record<SystemStatus, string> = {
  ok: 'Systems ok',
  warn: 'Degraded',
  down: 'Outage',
  maintenance: 'Maintenance',
}

const FOOTER_LINKS = [
  { href: '/pricing', label: 'Pricing', aria: 'Plans and pricing' },
  { href: '/help', label: 'Help', aria: 'Help center' },
  { href: '/contact', label: 'Contact', aria: 'Contact support' },
  { href: '/security', label: 'Security', aria: 'Security information' },
  { href: '/compliance', label: 'Compliance', aria: 'Compliance information' },
  { href: '/legal/privacy', label: 'Privacy', aria: 'Privacy policy' },
  { href: '/legal/terms', label: 'Terms', aria: 'Terms of service' },
  { href: '/legal/disclosures', label: 'Disclosures', aria: 'Product disclosures' },
] as const

export function Footer({ className = '', style = {} }: FooterProps) {
  const { token, isDark } = useTheme()
  const [isClient, setIsClient] = useState(false)
  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    setIsClient(true)
    setNow(new Date())
    const id = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(id)
  }, [])

  // Hydration-safe: fixed year on SSR, live year after mount
  const year = isClient ? new Date().getFullYear() : 2026
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION
  const buildId = process.env.NEXT_PUBLIC_BUILD_ID
  const status = resolveStatus(process.env.NEXT_PUBLIC_STATUS || 'ok')

  const equity = useMemo(
    () => (now ? getUsEquitySessionState(now) : null),
    [now]
  )

  const systemClass = useMemo(() => {
    if (status === 'down') return chrome.statusError
    if (status === 'warn' || status === 'maintenance') return chrome.statusWarn
    return chrome.statusOk
  }, [status])

  const statusLabel = STATUS_LABEL[status]

  const chromeVars = useMemo(
    () =>
      ({
        ['--chrome-bg' as string]: 'var(--shell-footer-bg, #FFFEFC)',
        ['--chrome-border' as string]:
          'var(--shell-footer-border, #D4C0AE)',
        ['--chrome-primary' as string]:
          'var(--color-primary, ' + token.colorPrimary + ')',
        ['--chrome-muted' as string]:
          'var(--color-text-secondary, ' +
          (token.colorTextSecondary || '#3A2A22') +
          ')',
        ['--chrome-text' as string]:
          'var(--color-text-primary, ' + (token.colorText || '#14100C') + ')',
        ['--chrome-on-primary' as string]:
          'var(--color-on-primary, ' +
          (token.colorTextLightSolid || (isDark ? '#1A1411' : '#FFFCFA')) +
          ')',
      }) as React.CSSProperties,
    [token, isDark]
  )

  const scrollToTop = useCallback((e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) e.preventDefault()

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const behavior: ScrollBehavior = prefersReduced ? 'auto' : 'smooth'

    try {
      window.scrollTo({ top: 0, left: 0, behavior })
      const scrollableElements = document.querySelectorAll(
        '[data-scrollable="true"], .custom-scrollbar, .ant-layout-content'
      )
      scrollableElements.forEach((element) => {
        if ((element as HTMLElement).scrollTop > 0) {
          element.scrollTo({ top: 0, behavior })
        }
      })
    } catch {
      window.scrollTo(0, 0)
    }
  }, [])

  return (
    <footer
      className={`${chrome.footer} ${className}`.trim()}
      style={{ ...chromeVars, ...style }}
      role="contentinfo"
      aria-label="Application footer"
    >
      <div className={chrome.footerInner}>
        <div className={chrome.footerBrandZone} aria-label="Copyright">
          <span className={chrome.footerBrand}>© {year} Mechagora</span>
          <span className={chrome.footerMeta}>All rights reserved</span>
          {(appVersion || buildId) && (
            <span
              className={chrome.footerMeta}
              title={buildId ? `Build ${buildId}` : undefined}
            >
              {appVersion ? `v${appVersion}` : ''}
              {buildId ? ` · ${String(buildId).slice(0, 7)}` : ''}
            </span>
          )}
        </div>

        <nav className={chrome.footerLinks} aria-label="Footer links">
          {FOOTER_LINKS.map(({ href, label, aria }) => (
            <Link key={href} href={href} aria-label={aria} className={chrome.link}>
              {label}
            </Link>
          ))}
        </nav>

        <div className={chrome.footerActions}>
          <div
            className={chrome.footerInstrument}
            role="group"
            aria-label="Market and system status"
          >
            <span
              className={`${chrome.footerStatusLink} ${
                equity?.isOpen ? chrome.statusOk : chrome.statusMuted
              }`}
              title={equity?.title ?? 'US equities regular session'}
            >
              {equity?.label ?? 'US session'}
            </span>
            <span className={chrome.footerInstrumentSep} aria-hidden>
              ·
            </span>
            <Link
              href="/status"
              aria-label={`System status: ${statusLabel}`}
              className={`${chrome.footerStatusLink} ${systemClass}`}
              title={`Platform status: ${statusLabel}`}
            >
              {statusLabel}
            </Link>
          </div>

          <button
            type="button"
            className={chrome.topBtn}
            onClick={scrollToTop}
            aria-label="Back to top"
          >
            <span className={chrome.topBtnCaret} aria-hidden="true" />
            <span>Top</span>
          </button>
        </div>
      </div>
    </footer>
  )
}

export default Footer
